import { injectable, inject } from 'inversify';
import JsonRpcDispatcher, { interceptors } from '@dealroadshow/json-rpc-dispatcher';
import DITypes from './DI/types';
import type { IAdapterCache, IDispatcherCache, IDispatcherFactory, IDispatcher } from './DI/interfaces';
import Container from '@framework/di/Container';
import { DataroomTenant } from '@dataroom/tenant/DataroomTenant';
import { DataroomTenantContextSymbol, type IDataroomTenantContext } from '@dataroom/tenant/types';
import addSessionData from '@users/session/interceptors/rpc/addSessionData';
import checkSession from '@users/session/interceptors/rpc/checkSession';
import parseWrappedError from '@framework/api/Rpc/parseWrappedError';
import OTelInstrument from '@framework/otel/OTelInstrument';
import { dataroomTwoFactorAuthentication } from './interceptors/dataroomTwoFactorAuthentication';
import { investorDisclaimerInterceptor } from './interceptors/investorDisclaimerInterceptor';

@injectable()
export class DispatcherFactory implements IDispatcherFactory {
  constructor(
    @inject(Container) private readonly container: Container,
    @inject(DITypes.AdapterCache) private readonly adapterCache: IAdapterCache,
    @inject(DITypes.DispatcherCache) private readonly dispatcherCache: IDispatcherCache,
  ) {}

  create(tenantCode?: DataroomTenant): IDispatcher {
    const tenant = (
      tenantCode ||
      (this.container as Container).get<IDataroomTenantContext>(DataroomTenantContextSymbol).tenant
    );

    if (!tenant) {
      throw new Error('Tenant not found');
    }

    if (!this.dispatcherCache.has(tenant)) {
      const dispatcher = new JsonRpcDispatcher(this.adapterCache.get(tenant));

      dispatcher
        .interceptRequest(addSessionData(this.container))
        .interceptResponse(checkSession(this.container))
        .interceptResponse(dataroomTwoFactorAuthentication(this.container, tenant))
        .interceptResponse(investorDisclaimerInterceptor(this.container, tenant))
        .interceptResponse(parseWrappedError())
        .interceptResponse(interceptors.response.transformErrorToException);

      OTelInstrument<typeof dispatcher.call>(
        dispatcher,
        'call',
        this.container,
        (request) => `rpc.call: ${ request.method }`,
        (request) => {
          return {
            attributes: {
              'rpc.method': request.method,
            },
          };
        },
      );

      this.dispatcherCache.set(tenant, dispatcher);
    }

    return this.dispatcherCache.get(tenant)!;
  }
}
